/*
Copyright (c) 2012-2022 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { StoreModelType } from "../../constants/misc"

export interface OrdersBreakDown {
  completed: number
  failed: number
  cancelled: number
}

export interface TransactionBreakdown {
  autoPaid: number
  manuallyPaid: number
  notPaid: number
}

export interface StoreDetailInit {
  storeID: StoreID
  name: string
  city: string
  type: string
  balance: PriceInMin
  revenue: PriceInMin
  earnings: PriceInMin
  advertisements: PriceInMin
  ordersBreakdown: OrdersBreakDown
  transactionsBreakdown: TransactionBreakdown
  active: boolean
  currencyCode: string
  timezone: string
  adjustments: PriceInMin
  grabPayID: GrabPayID
  merchantID: MerchantID
  merchantName: string
  tags: Array<string>
  specialMerchantType: string
  isIntegratedPartner: boolean
  useSpecialTooltip: boolean
}

export interface MerchantDetailInit {
  merchantID: MerchantID
  name: string
  country: string
  revenue: PriceInMin
  earnings: PriceInMin
  advertisements: PriceInMin
  ordersBreakdown: OrdersBreakDown
  transactionsBreakdown: TransactionBreakdown
  active: boolean
  currencyCode: string
  timezone: string
  adjustments: PriceInMin
  grabPayID: GrabPayID
  capital: string
}

export abstract class CommercialEntity {
  constructor(
    public readonly name: string,
    public readonly revenue: PriceInMin,
    public readonly earnings: PriceInMin,
    public readonly advertisements: PriceInMin,
    public readonly ordersBreakdown: OrdersBreakDown,
    public readonly transactionsBreakdown: TransactionBreakdown,
    public readonly adjustments: PriceInMin,
    public readonly active: boolean,
    public readonly currencyCode: string,
    public readonly timezone: string,
    public readonly grabPayID: GrabPayID,
  ) {}

  static totalOrderCntFromBreakdown(
    ordersBreakdown: OrdersBreakDown,
  ) {
    return (
      ordersBreakdown.cancelled +
      ordersBreakdown.failed +
      ordersBreakdown.completed
    )
  }

  static totalTransactionCntFromBreakdown(
    transactionsBreakdown: TransactionBreakdown,
  ) {
    return (
      transactionsBreakdown.autoPaid +
      transactionsBreakdown.manuallyPaid +
      transactionsBreakdown.notPaid
    )
  }
}

export class StoreDetail extends CommercialEntity {
  constructor({
    storeID,
    name,
    city,
    type,
    balance,
    revenue,
    earnings,
    advertisements,
    ordersBreakdown,
    transactionsBreakdown,
    active,
    currencyCode,
    timezone,
    adjustments,
    grabPayID,
    merchantID,
    merchantName,
    tags,
    specialMerchantType,
    isIntegratedPartner,
    useSpecialTooltip,
  }: StoreDetailInit) {
    super(
      name,
      revenue,
      earnings,
      advertisements,
      ordersBreakdown,
      transactionsBreakdown,
      adjustments,
      active,
      currencyCode,
      timezone,
      grabPayID,
    )
    this.storeID = storeID
    this.city = city
    this.type = type
    this.balance = balance
    this.totalOrderCnt = this.isIntegrated
      ? CommercialEntity.totalOrderCntFromBreakdown(
          this.ordersBreakdown,
        )
      : Number.NaN
    this.merchantID = merchantID
    this.merchantName = merchantName
    this.tags = tags
    this.specialMerchantType = specialMerchantType
    this.isIntegratedPartner = isIntegratedPartner
    this.useSpecialTooltip = useSpecialTooltip
    if (isIntegratedPartner) {
      this.tags.push(
        StoreModelType.integratedPartner,
      )
    }
  }

  get isIntegrated(): boolean {
    return this.type === StoreModelType.integrated
  }

  get modelTypeName(): string {
    if (this.isIntegrated)
      return StoreModelType.integrated
    return StoreModelType.concierge
  }

  public readonly storeID: StoreID

  public readonly city: string

  public readonly type: string

  public readonly totalOrderCnt: PriceInMin

  public readonly balance: PriceInMin

  public readonly merchantID: MerchantID

  public readonly merchantName: string

  public readonly tags: Array<string>

  public readonly specialMerchantType: string

  public readonly isIntegratedPartner: boolean

  public readonly useSpecialTooltip: boolean
}
export class MerchantDetail extends CommercialEntity {
  constructor({
    merchantID,
    name,
    country,
    revenue,
    earnings,
    advertisements,
    ordersBreakdown,
    transactionsBreakdown,
    active,
    currencyCode,
    timezone,
    adjustments,
    grabPayID,
    capital,
  }: MerchantDetailInit) {
    super(
      name,
      revenue,
      earnings,
      advertisements,
      ordersBreakdown,
      transactionsBreakdown,
      adjustments,
      active,
      currencyCode,
      timezone,
      grabPayID,
    )
    this.merchantID = merchantID
    this.country = country
    this.capital = capital
  }

  cloneWith({
    merchantID,
    name,
    country,
    revenue,
    earnings,
    advertisements,
    ordersBreakdown,
    transactionsBreakdown,
    active,
    currencyCode,
    timezone,
    adjustments,
    grabPayID,
    capital,
  }: Partial<MerchantDetailInit> = {}) {
    return new MerchantDetail({
      merchantID: merchantID ?? this.merchantID,
      name: name ?? this.name,
      country: country ?? this.country,
      revenue: revenue ?? this.revenue,
      earnings: earnings ?? this.earnings,
      advertisements:
        advertisements ?? this.advertisements,
      ordersBreakdown:
        ordersBreakdown ?? this.ordersBreakdown,
      transactionsBreakdown:
        transactionsBreakdown ??
        this.transactionsBreakdown,
      active: active ?? this.active,
      currencyCode:
        currencyCode ?? this.currencyCode,
      timezone: timezone ?? this.timezone,
      adjustments:
        adjustments ?? this.adjustments,
      grabPayID: grabPayID ?? this.grabPayID,
      capital: capital ?? this.capital,
    })
  }

  public readonly merchantID: MerchantID

  public readonly country: string

  public readonly capital: string
}
